import React, { useState } from 'react';
import { useTheme } from '../../../../Context/ThemeContext';
import { PurchaseItem } from '../PlayerHandler';

interface PlayerPurchasesProps {
  purchases: PurchaseItem[];
}

const PAGE_SIZE = 5;

const formatDate = (dateStr: string | undefined): string => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const PlayerPurchases: React.FC<PlayerPurchasesProps> = ({ purchases }) => {
  const { theme, isMobile } = useTheme();
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.ceil(purchases.length / PAGE_SIZE);
  const startIdx = (currentPage - 1) * PAGE_SIZE;
  const visiblePurchases = purchases.slice(startIdx, startIdx + PAGE_SIZE);

  const styles: Record<string, React.CSSProperties> = {
    container: {
      background: theme.card,
      borderRadius: '10px',
      padding: isMobile ? '12px' : '16px',
      marginBottom: '0px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    title: {
      fontSize: '16px',
      fontWeight: 700,
      color: theme.text,
      marginBottom: '12px',
    },
    row: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '10px 0',
      borderBottom: `1px solid ${theme.border}`,
    },
    productName: {
      fontSize: '14px',
      fontWeight: 500,
      color: theme.text,
    },
    date: {
      fontSize: '13px',
      color: theme.textSecondary,
    },
    empty: {
      color: theme.textSecondary,
      fontSize: '14px',
      fontStyle: 'italic' as const,
    },
    pagination: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '8px',
      paddingTop: '12px',
    },
    pageBtn: {
      padding: '4px 10px',
      fontSize: '13px',
      fontWeight: 500,
      border: `1px solid ${theme.inputBorder}`,
      borderRadius: '6px',
      background: theme.inputBg,
      color: theme.text,
      cursor: 'pointer',
    },
    pageBtnDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.title}>Purchases Made</div>
      {purchases.length === 0 ? (
        <div style={styles.empty}>No purchases</div>
      ) : (
        <>
          {visiblePurchases.map((p, i) => (
            <div key={startIdx + i} style={styles.row}>
              <span style={styles.productName}>{p.productName}</span>
              <span style={styles.date}>{formatDate(p.purchaseDate)}</span>
            </div>
          ))}
          {totalPages > 1 && (
            <div style={styles.pagination}>
              <button
                style={{ ...styles.pageBtn, ...(currentPage === 1 ? styles.pageBtnDisabled : {}) }}
                onClick={() => setCurrentPage(p => p - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span style={{ fontSize: '13px', color: theme.text }}>
                {currentPage} / {totalPages}
              </span>
              <button
                style={{ ...styles.pageBtn, ...(currentPage >= totalPages ? styles.pageBtnDisabled : {}) }}
                onClick={() => setCurrentPage(p => p + 1)}
                disabled={currentPage >= totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PlayerPurchases;
